/**
 * Script principal - main.js
 * Gère les jeux (1, 2, 3), les animations GSAP, la validation des interactions, etc.
 * Dernière mise à jour : 2025-07-17
 */

// Place ici les imports si utilisés via un bundler (sinon retirer)
import gsap from 'gsap';
import ScrollTrigger from "gsap/ScrollTrigger";
import interact from 'interactjs';
// import Swiper JS
import Swiper from 'swiper/bundle';
// import Swiper styles
import 'swiper/css';

// import tippy from 'tippy.js';
// import 'tippy.js/dist/tippy.css';
gsap.registerPlugin(ScrollTrigger);

//REVEAL ANIMATIONS
   function initRevealAnimations() {
        // Sélectionne tous les conteneurs avec la classe `.reveal`
        const revealContainers = document.querySelectorAll('.reveal');

        revealContainers.forEach(container => {
            // Sélectionne les enfants spécifiques (h1, h2, h3, h4, p, ul) dans chaque conteneur
            const textElements = container.querySelectorAll('h1, h2, h3, h4, p, ul, .reveal-item');
            const blockElements = container.querySelectorAll('.reveal-block');

            // Animation pour les éléments texte avec stagger
            if (textElements.length > 0) {
                gsap.set(textElements, { opacity: 0, y: 50 });
                gsap.timeline({
                    scrollTrigger: {
                        trigger: container,
                        start: 'top 85%',
                        end: 'bottom 10%',
                        toggleActions: 'play none none none',
                    },
                }).to(textElements, {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    ease: 'power3.out',
                    stagger: 0.1, // Décalage entre chaque élément texte
                });
            }

            // Animation pour les éléments blocs (par exemple, images ou autres contenus)
            if (blockElements.length > 0) {
                gsap.set(blockElements, { opacity: 0, scale: 0.95 });
                blockElements.forEach(block => {
                    gsap.timeline({
                        scrollTrigger: {
                            trigger: block,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }).to(block, {
                        opacity: 1,
                        scale: 1,
                        duration: 0.6,
                        ease: 'power3.out',
                    });
                });
            }
        });
    }

//TABS
    function kesacuirTabs() {
        const SELECTORS = {
            root: ".tabs",
            header: ".tabs__header",
            linkWrap: ".tab__link",
            btn: ".tablinks",
            panel: ".tab",
        };

        // Feature detect inert
        const hasInert = "inert" in HTMLElement.prototype;

        // Utility: hide/show panels safely
        const hidePanel = (el) => {
            if (!el) return;
            el.setAttribute("hidden", "");
            el.setAttribute("aria-hidden", "true");
            if (hasInert) el.inert = true;
        };
        const showPanel = (el) => {
            if (!el) return;
            el.removeAttribute("hidden");
            el.setAttribute("aria-hidden", "false");
            if (hasInert) el.inert = false;
        };

        // Try to deduce target id for a button
        const getTargetId = (btn) => {
            // 1) data-target
            if (btn.dataset.target) return btn.dataset.target;
            // 2) aria-controls
            const a = btn.getAttribute("aria-controls");
            if (a) return a;
            // 3) parse inline onclick="openTab(event, 'id')"
            const oc = btn.getAttribute("onclick");
            if (oc) {
            const m = oc.match(/openTab\s*\(\s*event\s*,\s*['"]([^'"]+)['"]\s*\)/i);
            if (m) return m[1];
            }
            // 4) href with hash (if changed later)
            const href = btn.getAttribute("href");
            if (href && href.includes("#")) return href.split("#").pop();
            return null;
        };

        // Activate a tab by id inside a given tabs root
        const activate = (root, targetId, { focus = true } = {}) => {
            const buttons = root.querySelectorAll(SELECTORS.btn);
            const panels  = root.querySelectorAll(SELECTORS.panel);

            let activeBtn = null;
            let activePanel = null;

            // Deactivate all
            buttons.forEach((b) => {
            b.setAttribute("aria-selected", "false");
            b.setAttribute("tabindex", "-1");
            b.classList.remove("is-active");
            });
            panels.forEach(hidePanel);

            // Activate chosen
            buttons.forEach((b) => {
            const id = getTargetId(b);
            if (id === targetId) {
                activeBtn = b;
            }
            });
            activePanel = root.querySelector(`${SELECTORS.panel}#${CSS.escape(targetId)}`) || root.querySelector(`${SELECTORS.panel}#tab-${CSS.escape(targetId)}`);

            if (activeBtn && activePanel) {
            activeBtn.setAttribute("aria-selected", "true");
            activeBtn.setAttribute("tabindex", "0");
            activeBtn.classList.add("is-active");
            showPanel(activePanel);
            if (focus) activeBtn.focus();
            // Update URL hash without scrolling
            if (history.replaceState) {
                const base = location.href.split("#")[0];
                history.replaceState(null, "", `${base}#${targetId}`);
            } else {
                location.hash = targetId;
            }
            }
        };

        // Keyboard navigation for a tablist
        const addKeyboard = (root, tablist) => {
            const getBtns = () => Array.from(root.querySelectorAll(SELECTORS.btn));
            tablist.addEventListener("keydown", (e) => {
            const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
            if (!keys.includes(e.key) && e.key !== " " && e.key !== "Enter") return;

            const btns = getBtns();
            const current = document.activeElement;
            const idx = btns.indexOf(current);
            if (idx === -1) return;

            let nextIdx = idx;

            if (e.key === "ArrowLeft") nextIdx = (idx - 1 + btns.length) % btns.length;
            if (e.key === "ArrowRight") nextIdx = (idx + 1) % btns.length;
            if (e.key === "Home") nextIdx = 0;
            if (e.key === "End") nextIdx = btns.length - 1;

            if (nextIdx !== idx) {
                e.preventDefault();
                btns[nextIdx].focus();
            }

            if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                const id = getTargetId(current);
                if (id) activate(root, id, { focus: true });
            }
            });
        };

        // Enhance all .tabs on the page
        document.querySelectorAll(SELECTORS.root).forEach((root) => {
            const header = root.querySelector(SELECTORS.header);
            const btnWrap = root.querySelector(SELECTORS.linkWrap);
            const buttons = root.querySelectorAll(SELECTORS.btn);
            const panels  = root.querySelectorAll(SELECTORS.panel);

            // Roles
            if (btnWrap) btnWrap.setAttribute("role", "tablist");
            buttons.forEach((b, i) => {
            b.setAttribute("role", "tab");
            b.setAttribute("aria-selected", "false");
            b.setAttribute("tabindex", "-1");

            // Wire click (prevent default if button is actually a <a>)
            b.addEventListener("click", (e) => {
                e.preventDefault();
                const id = getTargetId(b);
                if (id) activate(root, id, { focus: false });
            });

            // Ensure aria-controls
            const id = getTargetId(b);
            if (id) b.setAttribute("aria-controls", id);
            });

            panels.forEach((p) => {
            p.setAttribute("role", "tabpanel");
            p.setAttribute("tabindex", "-1"); // reachable for screen readers
            p.setAttribute("aria-hidden", "true");
            hidePanel(p);
            // Ensure labelledby
            const id = p.id;
            if (id) {
                const btn = Array.from(buttons).find((b) => getTargetId(b) === id);
                if (btn) {
                if (!btn.id) btn.id = `tab-${id}`;
                p.setAttribute("aria-labelledby", btn.id);
                }
            }
            });

            // Keyboard nav
            if (btnWrap) addKeyboard(root, btnWrap);

            // Initial active tab: from hash or first button
            const initialIdFromHash = location.hash ? location.hash.replace("#", "") : null;
            const firstId = buttons.length ? getTargetId(buttons[0]) : null;
            const initialId = (initialIdFromHash && (root.querySelector(`#${CSS.escape(initialIdFromHash)}`) || root.querySelector(`#tab-${CSS.escape(initialIdFromHash)}`)))
            ? initialIdFromHash
            : firstId;

            if (initialId) {
            activate(root, initialId, { focus: false });
            }

            // React to hash changes
            window.addEventListener("hashchange", () => {
            const h = location.hash.replace("#", "");
            if (h && (root.querySelector(`#${CSS.escape(h)}`) || root.querySelector(`#tab-${CSS.escape(h)}`))) {
                activate(root, h, { focus: true });
            }
            });
        });

        // Global fallback for existing inline handlers: openTab(event,'id')
        window.openTab = (evt, id) => {
            const root = evt?.currentTarget?.closest(SELECTORS.root) || document.querySelector(SELECTORS.root);
            if (root && id) activate(root, id, { focus: false });
        };
    }


//FONCTIONS AU RESIZE
    function handleResizeActions() {
        const popinElement = document.querySelector('.no-display');
        const transitionElement = document.querySelector('.page-transition');

        // Affiche ou masque la popin en fonction de la largeur de la fenêtre
        if (window.innerWidth < 1024) {
            popinElement?.classList.add('show-alert'); // Affiche la popin
        } else {
            popinElement?.classList.remove('show-alert'); // Cache la popin
        }

        // Repositionne .page-transition en haut
        if (transitionElement) {
            gsap.set(transitionElement, {
                y: window.innerHeight,
            });
        }
    }


// INIT SELECT2 (CF7 + selects simples)
    function initSelect2() {
        const $ = window.jQuery;
        if (!$ || !$.fn || !$.fn.select2) return;

        const applySelect2 = (ctx) => {
            $(ctx).find('select.select2[multiple]').each(function () {
                const $el = $(this);
                if ($el.hasClass('select2-initialized')) return;

                const hasEmpty = $el.find('option[value=""]').length > 0;
                const placeholder = hasEmpty ? $el.find('option[value=""]').text() : '';

                $el.select2({
                    width: '100%',
                    dropdownAutoWidth: true,
                    closeOnSelect: false,
                    placeholder,
                    allowClear: hasEmpty,
                    minimumResultsForSearch: Infinity,
                    language: {
                        noResults: function () { return 'Aucun résultat'; },
                    },
                });

                $el.addClass('select2-initialized');
            });

            $(ctx).find('select.select2-simple:not([multiple])').each(function () {
                const $el = $(this);
                if ($el.hasClass('select2-initialized')) return;

                const hasEmpty = $el.find('option[value=""]').length > 0;
                const placeholder = hasEmpty ? $el.find('option[value=""]').text() : '';

                $el.select2({
                    width: '100%',
                    dropdownAutoWidth: true,
                    placeholder,
                    allowClear: hasEmpty,
                });

                $el.addClass('select2-initialized');
            });
        };

        applySelect2(document);

        ['wpcf7submit', 'wpcf7invalid', 'wpcf7mailfailed', 'wpcf7mailsent'].forEach((evt) => {
            document.addEventListener(evt, (e) => applySelect2(e.target), false);
        });

        document.addEventListener('wpcf7reset', (e) => {
            const $target = $(e.target);
            $target.find('select.select2[multiple], select.select2-simple:not([multiple])')
                .val(null)
                .trigger('change');
            applySelect2(e.target);
        }, false);

        new MutationObserver((muts) => {
            muts.forEach((m) => {
                if (!m.addedNodes) return;
                m.addedNodes.forEach((n) => {
                    if (n.nodeType === 1) applySelect2(n);
                });
            });
        }).observe(document.body, { childList: true, subtree: true });
    }



function initTC() {
    initRevealAnimations();
    kesacuirTabs();
    initSelect2();
   // initLevelSelector();

}

document.addEventListener("DOMContentLoaded", initTC);

document.addEventListener('DOMContentLoaded', () => {
    handleResizeActions(); // Exécution au chargement
    window.addEventListener('resize', handleResizeActions); // Exécution lors du redimensionnement
});
